import { GeckoAnglePlotSetting } from 'types/cdfParams/geckoAnglePlotSetting'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import {
  parseArray,
  parseBoolean,
  parseDate,
  parseNumber,
  parseString,
} from 'utils/parsingHelpers'

import { GeckoEntryBase } from './geckoEntry'
import { RolodexEntryJson, RolodexEntryType } from './rolodexEntryFields'

export const AIRInspectionTypes = [
  'rug',
  'raut',
  'phased-array',
  'trilat',
  'lidar',
  'ie',
  'sasw',
  'upe',
  'emat',
  'mi',
  'art',
  'pulsed-eddy-current',
]
export type AIRInspectionType = (typeof AIRInspectionTypes)[number]
export const TraditionalInspectionTypes = [
  'manual',
  'penetrant-testing',
  'brick-and-tile-inspecton',
  'FRP',
  'external-visual',
  'internal-visual',
  'profile-radiography',
  'infrared-thermography',
  'spark-testing',
  'lift-lever-test',
]
export type TraditionalInspectionType =
  (typeof TraditionalInspectionTypes)[number]

export type GeckoInspectionType = AIRInspectionType | TraditionalInspectionType

export type GeckoAngleVariable = 'angle' | 'position' | 'tube'

export type GeckoInspectionUnits =
  | 'ft-in'
  | 'in-in'
  | 'm-mm'
  | 'mm-mm'
  | 'ft-mil'

export type GeckoNdtMethod =
  | 'ut'
  | 'aut'
  | 'paut'
  | 'emat'
  | 'mi'
  | 'acfm'
  | 'laser'
  | 'mlo'
  | 'pulsed-eddy-current'

export type GeckoValidationThickness =
  | 'thickness_upload'
  | 'thickness_rc'
  | 'thickness_bootsy'

export interface GeckoDsStatistics {
  invalid: GeckoMeasurement
  mean: GeckoMeasurement
  nominal: GeckoMeasurement
  non_invalidated_points: GeckoMeasurement
  not_measured: GeckoMeasurement
  num_bins: GeckoMeasurement
  num_bins_thickness_over_0: GeckoMeasurement
  num_points: GeckoMeasurement
  num_points_bootsy_score_over_075: GeckoMeasurement
  num_runs: GeckoMeasurement
  std: GeckoMeasurement
  timestamp: Date | undefined
  oldest_validation: Date | undefined
  newest_validation: Date | undefined
  uploaded_ascans: number | undefined
}

/**
 * Type guard to check if a string is a valid GeckoInspectionType
 * @param value The value to check
 * @returns True if the value is a valid GeckoInspectionType
 */
export function isGeckoInspectionType(
  value: unknown
): value is GeckoInspectionType {
  return (
    typeof value === 'string' &&
    [...AIRInspectionTypes, ...TraditionalInspectionTypes].includes(value)
  )
}

/**
 * An entry which represents the work performed for one component on a job.
 */
export class GeckoInspection extends GeckoEntryBase<
  {
    name: string | undefined
    date: Date
    data_source: string | undefined
    inspection_type: GeckoInspectionType | undefined
    ndt_method_type: GeckoNdtMethod | undefined
    ds_statistics: GeckoDsStatistics | undefined
    // this is not a gecko measurement unit, its more of a measurement system that is inputted and used in ds analyzer
    units: GeckoInspectionUnits | undefined
    num_points: number | undefined
    x_bin_size: number | undefined
    y_bin_size: number | undefined
    robot_types: string[] | undefined
    plot_rotation: number | undefined
    sensor_offset: number | undefined
    angle_variable: GeckoAngleVariable | undefined
    sensor_spacing: number | undefined
    angle_plot_settings: Array<GeckoAnglePlotSetting> | undefined
    validation_thickness: GeckoValidationThickness | undefined
    display_component_type_id: number | undefined
    low_threshold_relative_to_nominal: number | undefined
    high_threshold_relative_to_nominal: number | undefined
    vertical_runs: boolean | undefined
    vendor?: string
  },
  {
    organization: string
    site: string
    unit: string
    component: string
    job: string
    [RolodexEntryType.ASSET_MODEL]?: string
    ['sf-job']?: string
  },
  { image: string } | Record<string, never>
> {
  //----------------------------------------------------------------------------

  /**
   * A simple constructor, private so that a more descriptive factory method
   * (which can return undefined) is used instead
   * @param json The raw JSON to create an instance from
   * @private
   */
  private constructor(json: RolodexEntryJson) {
    const dsStats = json.data['ds-statistics'] as Record<string, unknown>

    const safeInspectionType = isGeckoInspectionType(json.data.inspection_type)
      ? json.data.inspection_type
      : undefined

    const allowedDataSources = ['rc', 'fulcrum']
    const safeDataSource = allowedDataSources.includes(
      parseString(json.data.data_source) ?? ''
    )
      ? parseString(json.data.data_source)
      : undefined

    if (json.data.inspection_type && !safeInspectionType)
      console.warn(`Unknown inspection_type "${json.data.inspection_type}"`)

    const parsedUnit =
      json.data.units === 'ft-in' ||
      json.data.units === 'in-in' ||
      json.data.units === 'm-mm' ||
      json.data.units === 'mm-mm' ||
      json.data.units === 'ft-mil'
        ? json.data.units
        : undefined

    const robotTypes = parseArray<string>(json.data.robot_types)

    const parsedAngleVariable =
      json.data.angle_variable === 'angle' ||
      json.data.angle_variable === 'position' ||
      json.data.angle_variable === 'tube'
        ? json.data.angle_variable
        : undefined

    const parsedAnglePlotSettings =
      (json.data.angle_plot_settings as
        | Array<GeckoAnglePlotSetting>
        | undefined) ?? []

    const transformedAnglePlotSettings = parsedAnglePlotSettings.map((aps) => {
      return GeckoAnglePlotSetting.GetOrCreate(
        aps.id,
        aps.camera_orientation,
        aps.query
      )
    })

    const parsedValidationThickness =
      json.data.validation_thickness === 'thickness_upload' ||
      json.data.validation_thickness === 'thickness_rc' ||
      json.data.validation_thickness === 'thickness_bootsy'
        ? json.data.validation_thickness
        : undefined

    const parsedNDTMethod =
      json.data.ndt_method_type === 'ut' ||
      json.data.ndt_method_type === 'aut' ||
      json.data.ndt_method_type === 'paut' ||
      json.data.ndt_method_type === 'emat' ||
      json.data.ndt_method_type === 'mi' ||
      json.data.ndt_method_type === 'acfm' ||
      json.data.ndt_method_type === 'laser' ||
      json.data.ndt_method_type === 'mlo' ||
      json.data.ndt_method_type === 'pulsed-eddy-current'
        ? json.data.ndt_method_type
        : undefined

    super(
      json,
      {
        name: parseString(json.data.name),
        date: parseDate(json.data.date) ?? new Date(),
        data_source: safeDataSource,
        inspection_type: safeInspectionType,
        vendor: parseString(json.data.vendor),
        units: parsedUnit,
        num_points: parseNumber(json.data.num_points) ?? 1,
        x_bin_size: parseNumber(json.data.x_bin_size),
        y_bin_size: parseNumber(json.data.y_bin_size),
        robot_types: robotTypes,
        plot_rotation: parseNumber(json.data.plot_rotation),
        sensor_offset: parseNumber(json.data.sensor_offset),
        angle_variable: parsedAngleVariable,
        sensor_spacing: parseNumber(json.data.sensor_spacing),
        angle_plot_settings: transformedAnglePlotSettings,
        ndt_method_type: parsedNDTMethod,
        validation_thickness: parsedValidationThickness,
        display_component_type_id: parseNumber(
          json.data.display_component_type_id
        ),
        vertical_runs: parseBoolean(json.data.vertical_runs) ?? true,
        low_threshold_relative_to_nominal: parseNumber(
          json.data.low_threshold_relative_to_nominal
        ),
        high_threshold_relative_to_nominal: parseNumber(
          json.data.high_threshold_relative_to_nominal
        ),
        ds_statistics: !dsStats
          ? undefined
          : {
              mean: GeckoMeasurement.CreateFromValue(
                parseNumber(dsStats.num_bins_thickness_over_0) ?? 0,
                GeckoMeasurementUnit.Inches
              ),
              nominal: GeckoMeasurement.CreateFromValue(
                parseNumber(dsStats.nominal) ?? 0,
                GeckoMeasurementUnit.Inches
              ),
              //----------------------------------------------------------------
              invalid: GeckoMeasurement.CreateFromValue(
                parseNumber(dsStats.invalid) ?? 0,
                GeckoMeasurementUnit.None
              ),
              non_invalidated_points: GeckoMeasurement.CreateFromValue(
                parseNumber(dsStats.non_invalidated_points) ?? 0,
                GeckoMeasurementUnit.None
              ),
              not_measured: GeckoMeasurement.CreateFromValue(
                parseNumber(dsStats.not_measured) ?? 0,
                GeckoMeasurementUnit.None
              ),
              num_bins: GeckoMeasurement.CreateFromValue(
                parseNumber(dsStats.num_bins) ?? 0,
                GeckoMeasurementUnit.None
              ),
              num_bins_thickness_over_0: GeckoMeasurement.CreateFromValue(
                parseNumber(dsStats.num_bins_thickness_over_0) ?? 0,
                GeckoMeasurementUnit.None
              ),
              num_points: GeckoMeasurement.CreateFromValue(
                parseNumber(dsStats.std) ?? 0,
                GeckoMeasurementUnit.None
              ),
              num_points_bootsy_score_over_075:
                GeckoMeasurement.CreateFromValue(
                  parseNumber(dsStats.num_points_bootsy_score_over_075) ?? 0,
                  GeckoMeasurementUnit.None
                ),
              //----------------------------------------------------------------
              num_runs: GeckoMeasurement.CreateFromValue(
                parseNumber(dsStats.num_runs) ?? 0,
                GeckoMeasurementUnit.None
              ),
              std: GeckoMeasurement.CreateFromValue(
                parseNumber(dsStats.std) ?? 0,
                GeckoMeasurementUnit.None
              ),
              timestamp: parseDate(dsStats.timestamp),
              newest_validation: parseDate(dsStats.newest_validation),
              oldest_validation: parseDate(dsStats.oldest_validation),
              uploaded_ascans: parseNumber(dsStats.uploaded_ascans),
            },
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
        unit: parseString(json.tags.unit) ?? '',
        component: parseString(json.tags.component) ?? '',
        job: parseString(json.tags.job) ?? '',
        [RolodexEntryType.ASSET_MODEL]: parseString(
          json.tags[RolodexEntryType.ASSET_MODEL]
        ),
        ['sf-job']: parseString(json.tags['sf-job']),
      },
      !!json.index && Object.prototype.hasOwnProperty.call(json.index, 'image')
        ? { image: json.index.image }
        : {}
    )
  }

  //----------------------------------------------------------------------------
  /**
   * A simple factory method which creates an inspection from raw JSON
   * @param json The raw JSON to create an instance from
   * @constructor
   */
  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoInspection(json)
  }
}
